import { useRef, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material';

interface MobileListCardProps {
  header: React.ReactNode;
  content: React.ReactNode;
  footer?: React.ReactNode;
  badge?: React.ReactNode;
  onClick?: () => void;
  copyText?: string;
  sx?: SxProps<Theme>;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'clickable' && prop !== 'pressing',
})<{ clickable?: boolean; pressing?: boolean }>(({ theme, clickable, pressing }) => ({
  position: 'relative',
  borderRadius: 16,
  border: `1px solid ${theme.palette.surface.glassBorder}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.2s ease',
  minHeight: 44,

  ...(clickable && {
    cursor: 'pointer',
  }),

  ...(pressing && {
    '@media (prefers-reduced-motion: no-preference)': {
      transform: 'scale(0.97)',
    },
  }),
}));

const BadgeContainer = styled(Box)({
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 1,
});

export function MobileListCard({
  header,
  content,
  footer,
  badge,
  onClick,
  copyText,
  sx,
}: MobileListCardProps) {
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [pressing, setPressing] = useState(false);

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  // Track whether a long-press copy fired so we can suppress the synthesized click
  const longPressFired = useRef(false);

  const clearTimers = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (feedbackTimer.current) {
      clearTimeout(feedbackTimer.current);
      feedbackTimer.current = null;
    }
    setPressing(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!copyText) return;

      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };

      // Visual feedback after 200ms
      feedbackTimer.current = setTimeout(() => {
        setPressing(true);
      }, 200);

      // Copy after 500ms
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        void navigator.clipboard.writeText(copyText).then(
          () => { setSnackbar({ open: true, message: 'Booking details copied!', severity: 'success' }); },
          () => { setSnackbar({ open: true, message: 'Copy failed', severity: 'error' }); }
        ).finally(() => { setPressing(false); });
      }, 500);
    },
    [copyText]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartPos.current) return;

      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - touchStartPos.current.x);
      const dy = Math.abs(touch.clientY - touchStartPos.current.y);

      // Cancel if finger moved more than 10px
      if (dx > 10 || dy > 10) {
        clearTimers();
        touchStartPos.current = null;
      }
    },
    [clearTimers]
  );

  const handleTouchEnd = useCallback(() => {
    clearTimers();
    touchStartPos.current = null;
  }, [clearTimers]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Suppress synthesized click that follows a long-press copy gesture
      if (longPressFired.current) {
        longPressFired.current = false;
        e.stopPropagation();
        return;
      }
      onClick?.();
    },
    [onClick]
  );

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <CardRoot
        clickable={!!onClick}
        pressing={pressing}
        onClick={copyText ? handleClick : onClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        sx={sx}
      >
        {badge && <BadgeContainer>{badge}</BadgeContainer>}

        {/* Header */}
        <Box sx={{ p: 2, pb: 0 }}>{header}</Box>

        {/* Content */}
        <Box sx={{ px: 2, py: 1 }}>{content}</Box>

        {/* Footer */}
        {footer && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 1,
            }}
          >
            {footer}
          </Box>
        )}
      </CardRoot>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
